import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";

/**
 * 62 × 100mm Brother QL label format (portrait).
 * 62mm width × 100mm height @ ~300dpi = 732 × 1181 px equivalent.
 * Using mm units in CSS for accurate physical print sizing.
 */
export default function RollLabel({ roll, product, manufacturer, series, codeType = "qr", scanTarget }) {
  if (!roll || !product) return null;
  const exp = new Date(roll.expiry_date);
  const today = new Date();
  const daysToExp = Math.ceil((exp - today) / 86400000);
  const isExpiring = daysToExp >= 0 && daysToExp <= 90;
  const isExpired = daysToExp < 0;
  const qrValue = scanTarget || roll.sku;
  const isIncoming = roll.status === "incoming";

  return (
    <div className="vs-label" data-testid={`label-${roll.id}`}>
      <style>{`
        @page { size: 62mm 100mm; margin: 0; }
        @media print {
          html, body { background: white !important; }
          .vs-label-page-break { page-break-after: always; }
        }
        .vs-label {
          width: 62mm;
          height: 100mm;
          padding: 3mm 3.5mm;
          box-sizing: border-box;
          background: white;
          color: #0f172a;
          font-family: 'IBM Plex Sans', 'Helvetica Neue', sans-serif;
          display: flex;
          flex-direction: column;
          gap: 2mm;
          border: 0.3mm solid #cbd5e1;
        }
        .vs-label__header { display: flex; justify-content: space-between; align-items: flex-start; gap: 2mm; }
        .vs-label__brand { font-size: 7pt; letter-spacing: 0.15em; text-transform: uppercase; color: #64748b; }
        .vs-label__series { font-size: 7pt; letter-spacing: 0.1em; text-transform: uppercase; color: #475569; margin-top: 0.5mm; }
        .vs-label__name { font-family: 'Outfit', 'Helvetica Neue', sans-serif; font-size: 14pt; font-weight: 700; line-height: 1.05; margin-top: 1mm; word-break: break-word; }
        .vs-label__length { font-family: 'Outfit', 'Helvetica Neue', sans-serif; font-size: 14pt; font-weight: 700; line-height: 1.05; margin-top: 0.5mm; color: #2563eb; }
        .vs-label__swatch { width: 18mm; height: 8mm; border: 0.3mm solid #94a3b8; flex-shrink: 0; }
        .vs-label__sku { font-family: 'JetBrains Mono', monospace; font-size: 8pt; word-break: break-all; line-height: 1.2; padding: 1.5mm 2mm; background: #f1f5f9; border-radius: 0.8mm; }
        .vs-label__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.2mm 2.5mm; font-size: 7pt; }
        .vs-label__field-label { color: #64748b; text-transform: uppercase; letter-spacing: 0.08em; font-size: 6pt; }
        .vs-label__field-value { font-family: 'JetBrains Mono', monospace; font-size: 8pt; font-weight: 600; }
        .vs-label__exp-warn { color: #b45309 !important; }
        .vs-label__exp-bad { color: #b91c1c !important; }
        .vs-label__incoming-badge { display: inline-block; padding: 0.5mm 1.5mm; background: #fef3c7; color: #92400e; border: 0.2mm solid #fcd34d; font-size: 6pt; letter-spacing: 0.15em; text-transform: uppercase; border-radius: 0.5mm; }
        .vs-label__code { display: flex; justify-content: center; align-items: center; flex: 1; min-height: 20mm; padding: 1mm 0; }
        .vs-label__code svg { display: block; width: 100%; max-width: 46mm; height: auto !important; }
        .vs-label__footer { font-size: 6pt; color: #94a3b8; text-align: center; letter-spacing: 0.1em; text-transform: uppercase; }
      `}</style>

      <div className="vs-label__header">
        <div>
          <div className="vs-label__brand">{manufacturer?.name || "—"}{series ? " · " + series.name : ""}</div>
          <div className="vs-label__name">{product.name}</div>
          <div className="vs-label__length">{(isIncoming ? roll.initial_length_m : roll.current_length_m).toFixed(2)} m</div>
          {isIncoming && <div className="vs-label__incoming-badge" style={{ marginTop: "1mm" }}>Incoming</div>}
        </div>
      </div>

      <div className="vs-label__sku">{roll.sku}</div>

      <div className="vs-label__grid">
        <div>
          <div className="vs-label__field-label">Width</div>
          <div className="vs-label__field-value">{roll.width_m.toFixed(2)}m</div>
        </div>
        <div>
          <div className="vs-label__field-label">Batch</div>
          <div className="vs-label__field-value">{roll.batch_number}</div>
        </div>
        <div>
          <div className="vs-label__field-label">Expires</div>
          <div className={`vs-label__field-value ${isExpired ? "vs-label__exp-bad" : isExpiring ? "vs-label__exp-warn" : ""}`}>
            {roll.expiry_date}
          </div>
        </div>
        <div>
          <div className="vs-label__field-label">Location</div>
          <div className="vs-label__field-value">{roll.location || "—"}</div>
        </div>
      </div>

      <div className="vs-label__code">
        {codeType === "barcode" ? (
          <Barcode value={roll.sku} format="CODE128" width={1.4} height={48} fontSize={10} margin={0} displayValue={false} />
        ) : (
          <QRCodeSVG value={qrValue} size={132} level="M" includeMargin={false} />
        )}
      </div>
    </div>
  );
}
